
import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../contexts/LanguageContext';
import Drawer, { IDefaultDrawerProps } from '../../../../components/Drawer';
import { ActionButton } from '../../../../components/Button';
import SelectField from '../../../../components/SelectField';
import FormHelper from '../../../../helpers/FormHelper';
import { useAuthContext } from '../../../../contexts/AuthContext';
import DateField from '../../../../components/DateField';
import DefaultConstants from '../../../../data/Constants';
import useState from '../../../../hooks/useState';
import DateTimeHelper from '../../../../helpers/DateTimeHelper';
import { RegistrationStatusData, RegistrationStatusEnum } from '../../../../data/Enums';


interface IRoomBlockingFilterProps extends IDefaultDrawerProps {
    handleApplyFilters: (formData: FormData) => void;
}

interface IStateValues {
    roomId: number;
    startDate: Date | undefined;
    endDate: Date | undefined;
    typeId: number;
    registrationStatus: number[];
}


const now = DateTimeHelper.ToDate(undefined);

const Filters: React.FC<IRoomBlockingFilterProps> = ({
    open,
    handleClose,
    handleApplyFilters
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        roomId: 0,
        startDate: now,
        endDate: now,
        typeId: 0,
        registrationStatus: [RegistrationStatusEnum.Active]
    });


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const formData = FormHelper.Create(event);
        handleApplyFilters(formData);
        handleClose();
        // eslint-disable-next-line
    }, []);


    const footer = useMemo(() => {
        return <ActionButton
            action='apply'
            fullWidth={true}
            className='text-alwaysshow'
            type='submit'
        />;
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <Drawer
            anchor='right'
            open={open}
            handleClose={handleClose}
            title={translate('filter')}
            footer={footer}
            onSubmit={handleSubmit}
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='roomId'
                        value={state.values.roomId}
                        required={false}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={[{ value: 0, text: translate('all') }].concat((enterprise?.partnerPms.rooms ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={state.values.startDate}
                        onChange={value => state.update('startDate', value)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={state.values.endDate}
                        onChange={value => state.update('endDate', value)}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('blockingType')}
                        name='typeId'
                        value={state.values.typeId}
                        required={false}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={[{ value: 0, text: translate('all') }].concat((enterprise?.partnerPms.roomBlockingTypes ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <SelectField
                        label={translate('situation')}
                        name='registrationStatus'
                        value={state.values.registrationStatus}
                        multiple={true}
                        required={true}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Object.values(RegistrationStatusData).filter(s => s.visible).map(s => {
                            return {
                                value: s.id,
                                text: translate(s.name)
                            };
                        })}
                    />
                </Grid>
            </Grid>
        </Drawer>
    );
}

export default Filters;